import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import SubsWall, { STYLE_PALETTES, type SubTile, type TileStyle } from "../components/SubsWall";
import "./LandingV4.css";

function makeHeroTiles(): SubTile[] {
  const seed: Array<Partial<SubTile> & { name: string; message: string; monthlyCents: number; style: TileStyle }> = [
    { name: "KAIROS",      message: "first one in. shipping forever.",          monthlyCents: 500,   style: "spray" },
    { name: "ZINC",        message: "$5/mo for a forever spot.",                monthlyCents: 500,   style: "chalk" },
    { name: "lo-fi-ghost", message: "playing your VOD on loop while I code",    monthlyCents: 1000,  style: "neon", blink: true },
    { name: "FRACTAL",     message: "the soundtrack saved my run",              monthlyCents: 2500,  style: "neon", blink: true, anniversaryMonths: 7 },
    { name: "VEXLINE",     message: "watching since the API days",              monthlyCents: 5000,  style: "carved", badge: "shield" },
    { name: "BIGSHOT",     message: "yes I bought the biggest tile.",           monthlyCents: 10000, style: "neon", blink: true, badge: "crown" },
    { name: "AURORA",      message: "day-one founder. forever.",                monthlyCents: 25000, style: "neon", blink: true, badge: "star", founderNumber: 1, anniversaryMonths: 12 },
  ];
  return seed.map((s, i) => ({
    id: `h${i}`,
    name: s.name,
    message: s.message,
    style: s.style,
    color: STYLE_PALETTES[s.style][(i + 1) % 8],
    xPct: 14 + ((i * 73) % 74),
    yPct: 22 + ((i * 41) % 64),
    monthlyCents: s.monthlyCents,
    anniversaryMonths: s.anniversaryMonths ?? 0,
    founderNumber: s.founderNumber ?? null,
    boostUntil: null,
    fadeStartedAt: null,
    badge: s.badge ?? null,
    blink: s.blink ?? false,
  }));
}

function useCountUp(target: number, durationMs = 1400): number {
  const [n, setN] = useState(0);
  useEffect(() => {
    const start = performance.now();
    let raf = 0;
    const tick = (t: number): void => {
      const p = Math.min(1, (t - start) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return n;
}

export default function LandingV4(): JSX.Element {
  const [tiles] = useState<SubTile[]>(makeHeroTiles);
  const supporters = useCountUp(2147);
  const paidOut = useCountUp(184320);

  return (
    <div className="pp-page lv4">
      <nav className="lv4-nav pp-container">
        <Link to="/" className="lv4-brand">
          <span className="lv4-brand-glyph"></span> SubsWall
        </Link>
        <div className="lv4-nav-right">
          <a href="#how">How</a>
          <a href="#why">Why</a>
          <a href="#pricing">Pricing</a>
          <Link to="/login">Sign in</Link>
          <Link to="/login" className="pp-btn pp-btn-primary pp-btn-sm">Claim handle →</Link>
        </div>
      </nav>

      <header className="lv4-hero pp-container">
        <div className="pp-eyebrow pp-fade pp-fade-d1">For creators · YouTube · Twitch · Podcast · Substack</div>
        <h1 className="pp-display lv4-h1 pp-fade pp-fade-d2">
          The wall<br/>
          <span className="lv4-h1-em">that pays you</span><br/>
          forever.
        </h1>
        <p className="pp-lead lv4-lead pp-fade pp-fade-d3">
          Recurring subscriptions, made public. Your supporters sign your wall once
          and stay named on your channel for as long as they support you. Patreon
          makes you a content factory. We make your fans famous.
        </p>
        <div className="lv4-hero-ctas pp-fade pp-fade-d4">
          <Link to="/login" className="pp-btn pp-btn-primary pp-btn-lg">
            Claim your wall →
          </Link>
          <Link to="/demo" className="pp-btn pp-btn-ghost pp-btn-lg">
            See a live wall
          </Link>
        </div>

        <div className="lv4-stats pp-fade pp-fade-d4">
          <div>
            <div className="pp-mono lv4-stat-num">{supporters.toLocaleString()}</div>
            <div className="lv4-stat-lbl">supporters on walls</div>
          </div>
          <div>
            <div className="pp-mono lv4-stat-num">${paidOut.toLocaleString()}</div>
            <div className="lv4-stat-lbl">paid to creators</div>
          </div>
          <div>
            <div className="pp-mono lv4-stat-num">4.9<small>%</small></div>
            <div className="lv4-stat-lbl">flat · no upsells</div>
          </div>
        </div>

        {/* THE WALL — dark island floating on white, maximum contrast */}
        <div className="lv4-hero-wall pp-fade pp-fade-d4">
          <div className="lv4-wall-frame">
            <SubsWall tiles={tiles} theme="glow" aspectRatio={20 / 9} />
          </div>
          <div className="lv4-hero-wall-caption">
            <span className="lv4-pulse"></span>
            A real SubsWall · drag your own tile when you sign on
          </div>
        </div>
      </header>

      <section className="lv4-bento pp-container" id="how">
        <div className="pp-eyebrow">How it works</div>
        <h2 className="pp-display lv4-h2">Three steps. No newsletter.</h2>

        <div className="lv4-bento-grid">
          <div className="lv4-bento-card">
            <div className="lv4-step">01</div>
            <h3>Claim your handle.</h3>
            <p>Sign in. Pick a name. Link Stripe. <code>subswall.com/yourname</code> goes live in 30 seconds.</p>
            <div className="lv4-bento-mock">
              <span className="lv4-mono-input">subswall.com/<em>your-handle</em></span>
            </div>
          </div>

          <div className="lv4-bento-card">
            <div className="lv4-step">02</div>
            <h3>Fans sign in 30 seconds.</h3>
            <p>One screen. Name + line + tile style + Stripe checkout. They're on, named, recurring.</p>
            <div className="lv4-bento-mock lv4-bento-mock-tiles">
              <span className="lv4-mock-tile lv4-mock-t1">SARAH K.</span>
              <span className="lv4-mock-tile lv4-mock-t2">MARCUS</span>
              <span className="lv4-mock-tile lv4-mock-t3">AURORA</span>
            </div>
          </div>

          <div className="lv4-bento-card">
            <div className="lv4-step">03</div>
            <h3>The wall does the rest.</h3>
            <p>Cancellation fades over 7 days. Top tiers look unmistakably bigger. Anniversaries earn gold dots.</p>
          </div>

          <div className="lv4-bento-card lv4-bento-wide">
            <div className="lv4-step">⌘</div>
            <h3>Embed anywhere.</h3>
            <p>YouTube About page. Video descriptions. Your own site. One iframe, updates live, signature handshake with your brand.</p>
            <pre className="lv4-code">{`<iframe src="https://subswall.com/you/embed"
        width="100%" height="600"
        loading="lazy"/>`}</pre>
          </div>

          <div className="lv4-bento-card lv4-bento-accent">
            <div className="lv4-step lv4-step-accent">$</div>
            <h3>4.9% flat.</h3>
            <p>No tiers for creators. No per-supporter fee. No "Pro plan." Stripe processes; we take 4.9%; you keep 95.1%.</p>
          </div>
        </div>
      </section>

      <section className="lv4-vs pp-container" id="why">
        <div className="pp-eyebrow">Why not Patreon</div>
        <h2 className="pp-display lv4-h2">
          Patreon makes you a content factory.<br />
          <span className="lv4-h1-em">We make your fans famous.</span>
        </h2>

        <div className="lv4-vs-grid">
          <div className="lv4-vs-col">
            <div className="lv4-vs-col-head">Patreon</div>
            <ul>
              <li className="lv4-x">Tiers, posts, DMs, comments — admin work forever</li>
              <li className="lv4-x">Hidden behind a paywall · fans invisible</li>
              <li className="lv4-x">8–12% take · Pro plan upsells</li>
              <li className="lv4-x">If you stop posting, you stop earning</li>
            </ul>
          </div>
          <div className="lv4-vs-col lv4-vs-us">
            <div className="lv4-vs-col-head lv4-vs-us-head">SubsWall</div>
            <ul>
              <li className="lv4-tick">Zero admin · the wall is the product</li>
              <li className="lv4-tick">Public wall · cancellation visibly fades</li>
              <li className="lv4-tick">4.9% flat · forever · no upsells</li>
              <li className="lv4-tick">Subscribers stay even when you don't post</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="lv4-pricing pp-container" id="pricing">
        <div className="lv4-pricing-text">
          <div className="pp-eyebrow">Pricing</div>
          <h2 className="pp-display lv4-h2">One number.<br /><span className="lv4-h1-em">4.9%</span> of what you earn.</h2>
          <p className="pp-lead">
            Plus Stripe's standard processing. No monthly minimum. No per-supporter fee.
            No "Pro plan." Just one line.
          </p>
          <Link to="/login" className="pp-btn pp-btn-primary pp-btn-lg">Start in 60 seconds →</Link>
        </div>

        <div className="lv4-pricing-card">
          <div className="pp-eyebrow">Supporter pricing</div>
          <h3 className="lv4-ladder-title">Tiers grow into something real.</h3>
          <div className="lv4-ladder">
            <div><span>☕</span><strong className="pp-mono">$5</strong><em>a coffee</em></div>
            <div><span>🍕</span><strong className="pp-mono">$10</strong><em>a pizza</em></div>
            <div><span>🍣</span><strong className="pp-mono">$25</strong><em>sushi</em></div>
            <div><span>🍷</span><strong className="pp-mono">$50</strong><em>a bottle of wine</em></div>
            <div><span>🍽️</span><strong className="pp-mono">$100</strong><em>a tasting menu</em></div>
            <div><span>✈️</span><strong className="pp-mono">$250</strong><em>a weekend trip</em></div>
          </div>
          <div className="lv4-ladder-foot">Higher tiers unlock bigger tiles, gold foil, and a permanent slot.</div>
        </div>
      </section>

      <section className="lv4-final pp-container">
        <h2 className="pp-display lv4-final-h">
          Your channel deserves a <span className="lv4-h1-em">monument</span>,<br />
          not a feed.
        </h2>
        <p className="pp-lead">60 seconds. Claim your handle.</p>
        <div className="lv4-final-ctas">
          <Link to="/login" className="pp-btn pp-btn-primary pp-btn-lg">Claim your wall →</Link>
          <Link to="/demo" className="pp-btn pp-btn-ghost pp-btn-lg">See a live wall</Link>
        </div>
      </section>

      <footer className="lv4-footer">
        <div className="pp-container lv4-footer-inner">
          <div className="lv4-brand">
            <span className="lv4-brand-glyph"></span> SubsWall
          </div>
          <div className="lv4-footer-meta">
            <span>4.9% take · 95.1% to creators</span>
            <span>·</span>
            <Link to="/terms">Terms</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/contact">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
